import { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import GenesisAccountCreator from './GenesisAccountCreator'; 
import LeadIntakeForm from './LeadIntakeForm'; 
import DealDrawer from './DealDrawer'; // NEW: Imported Deal Drawer
import { useAuth } from '../../context/AuthContext';
import { useTenant } from '../../context/TenantContext'; 
import { getTenantLeads } from '../../services/crmService'; 
import { db } from '../../services/firebase';
import { doc, updateDoc, deleteDoc, serverTimestamp, getFirestore } from 'firebase/firestore';

export default function PipelineBoard({ isOperatorView = false }) {
  const [showGenesisIntake, setShowGenesisIntake] = useState(false);
  const [showLeadIntake, setShowLeadIntake] = useState(false);
  
  // Schedule Modal State
  const [showScheduleModal, setShowScheduleModal] = useState(false);
  const [isScheduling, setIsScheduling] = useState(false);
  const [scheduleForm, setScheduleForm] = useState({
    leadId: '',
    date: '',
    time: '12:00',
    assignedRep: '',
    clientEmail: ''
  });
  
  const [isLoading, setIsLoading] = useState(true);
  const navigate = useNavigate();
  
  const { currentUser } = useAuth();
  const { tenantId, loading: tenantLoading } = useTenant(); 

  const [columns, setColumns] = useState({
    newLeads: { id: 'newLeads', title: 'New Leads', colorClass: 'bg-slate-200 dark:bg-slate-700', borderClass: 'border-slate-300 dark:border-slate-700', textClass: 'text-slate-700 dark:text-slate-300', leads: [] },
    contacted: { id: 'contacted', title: 'Contacted / Discovery', colorClass: 'bg-amber-100 dark:bg-amber-500/20', borderClass: 'border-amber-300 dark:border-amber-500/30', textClass: 'text-amber-800 dark:text-amber-400', leads: [] },
    negotiating: { id: 'negotiating', title: 'Proposal Sent', colorClass: 'bg-blue-100 dark:bg-blue-500/20', borderClass: 'border-blue-300 dark:border-blue-500/30', textClass: 'text-blue-800 dark:text-blue-400', leads: [] },
    closedWon: { id: 'closedWon', title: 'Deal Desk Review', colorClass: 'bg-emerald-100 dark:bg-emerald-500/20', borderClass: 'border-emerald-300 dark:border-emerald-500/30', textClass: 'text-emerald-800 dark:text-emerald-400', leads: [] }
  });

  const columnOrder = ['newLeads', 'contacted', 'negotiating', 'closedWon'];

  // --- EXECUTIVE HUD STATE ---
  const [filterDealSize, setFilterDealSize] = useState('all'); // all, smb, enterprise
  const [filterRep, setFilterRep] = useState('all');

  // --- DEAL DRAWER STATE ---
  const [activeDrawerLead, setActiveDrawerLead] = useState(null);

  // Calendar State
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [allLeads, setAllLeads] = useState([]); 

  const fetchLivePipeline = useCallback(async () => {
    if (tenantLoading || !tenantId) return; 

    setIsLoading(true);
    try {
      const operatorUid = isOperatorView ? currentUser?.uid : null;
      let fetchedDeals = await getTenantLeads(tenantId);

      if (operatorUid) {
        const isMatt = currentUser?.email?.toLowerCase().includes('matt');
        fetchedDeals = fetchedDeals.filter(deal => {
          if (deal.rep_uid === operatorUid) return true;
          if (!deal.rep_uid && isMatt) return true; 
          return false;
        });
      }

      const freshCols = {
        newLeads: { id: 'newLeads', title: 'New Leads', colorClass: 'bg-slate-200 dark:bg-slate-700', borderClass: 'border-slate-300 dark:border-slate-700', textClass: 'text-slate-700 dark:text-slate-300', leads: [] },
        contacted: { id: 'contacted', title: 'Contacted / Discovery', colorClass: 'bg-amber-100 dark:bg-amber-500/20', borderClass: 'border-amber-300 dark:border-amber-500/30', textClass: 'text-amber-800 dark:text-amber-400', leads: [] },
        negotiating: { id: 'negotiating', title: 'Proposal Sent', colorClass: 'bg-blue-100 dark:bg-blue-500/20', borderClass: 'border-blue-300 dark:border-blue-500/30', textClass: 'text-blue-800 dark:text-blue-400', leads: [] },
        closedWon: { id: 'closedWon', title: 'Deal Desk Review', colorClass: 'bg-emerald-100 dark:bg-emerald-500/20', borderClass: 'border-emerald-300 dark:border-emerald-500/30', textClass: 'text-emerald-800 dark:text-emerald-400', leads: [] }
      };

      const mappedLeads = [];

      fetchedDeals.forEach(deal => {
        let rawStatus = deal.stage || deal.status || 'newLeads';
        let stage = rawStatus;
        
        if (rawStatus === 'Paid Lead - Unscheduled' || rawStatus === 'New Lead') {
          stage = 'newLeads';
        }
        
        // Track true dates for Algorithmic Decay
        let rawLastTouch = new Date();
        let touchDate = 'Just Now';
        
        if (deal.last_touch?.toDate) {
          rawLastTouch = deal.last_touch.toDate();
          touchDate = rawLastTouch.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
        } else if (deal.createdAt?.toDate) {
          rawLastTouch = deal.createdAt.toDate();
          touchDate = rawLastTouch.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
        }

        let scheduledDateObj = null;
        if (deal.scheduled_date?.toDate) {
          scheduledDateObj = deal.scheduled_date.toDate();
        } else if (deal.scheduledDate) {
          scheduledDateObj = new Date(deal.scheduledDate);
        }

        const personalName = deal.firstName ? `${deal.firstName} ${deal.lastName || ''}`.trim() : null;
        const extractedEmail = deal.clientEmail || deal.email || deal.contactEmail || deal.contact_email || (deal.contact && deal.contact.includes('@') ? deal.contact : '');
        const mrrVal = Number(deal.mrr_value || deal.mrr || deal.amountPaid || 0);

        const parsedLead = {
          id: deal.id,
          stage: stage,
          company: deal.company || deal.entityName || deal.company_name || personalName || 'Unknown Entity',
          contact: deal.primaryContact || deal.contact_name || personalName || 'Unknown Contact',
          clientEmail: extractedEmail,
          mrr: mrrVal,
          rep: deal.rep_name || (deal.rep_uid ? 'Unassigned' : 'System Auto'),
          assignedRep: deal.assignedRep || null,
          lastTouch: touchDate,
          rawLastTouch: rawLastTouch,
          scheduledDateObj,
          packageType: deal.packageType || null,
          isUnscheduled: rawStatus === 'Paid Lead - Unscheduled',
          sourceCollection: deal.sourceCollection || (deal.sourceType === 'Web' ? 'pipeline' : 'crm_leads'),
          sourceType: deal.sourceType,
          serviceMatrix: deal.serviceMatrix || null, // Capture saved services
          customPricing: deal.customPricing || null // Capture custom bespoke pricing
        };

        if (freshCols[stage]) {
          freshCols[stage].leads.push(parsedLead);
        }
        mappedLeads.push(parsedLead);
      });

      setColumns(freshCols);
      setAllLeads(mappedLeads);

    } catch (error) {
      console.error("Failed to sync pipeline with Firebase:", error);
    } finally {
      setIsLoading(false);
    }
  }, [currentUser, isOperatorView, tenantId, tenantLoading]);

  useEffect(() => {
    if (currentUser) {
      fetchLivePipeline();
    }
  }, [currentUser, fetchLivePipeline]);

  // --- KINETIC HUD FILTERS ---
  const getFilteredColumns = () => {
    const filteredCols = JSON.parse(JSON.stringify(columns)); // deep clone
    
    columnOrder.forEach(colId => {
      filteredCols[colId].leads = filteredCols[colId].leads.filter(lead => {
        let match = true;
        if (filterDealSize === 'smb' && lead.mrr >= 5000) match = false;
        if (filterDealSize === 'enterprise' && lead.mrr < 5000) match = false;
        if (filterRep !== 'all' && lead.rep !== filterRep) match = false;
        return match;
      });
    });
    
    return filteredCols;
  };

  const filteredColumns = getFilteredColumns();
  const uniqueReps = [...new Set(allLeads.map(l => l.rep))].filter(Boolean);

  const calculateTotalPipeline = () => {
    let total = 0;
    columnOrder.forEach(colId => {
      total += filteredColumns[colId].leads.reduce((sum, lead) => sum + lead.mrr, 0);
    });
    return total;
  };

  const calculateColumnTotal = (colId) => {
    return filteredColumns[colId].leads.reduce((sum, lead) => sum + lead.mrr, 0);
  };

  const calculateClosedWon = () => {
    return filteredColumns['closedWon'].leads.reduce((sum, lead) => sum + lead.mrr, 0);
  };

  // --- NATIVE HTML5 DRAG & DROP LOGIC ---
  const handleDragStart = (e, lead) => {
    e.dataTransfer.setData('application/json', JSON.stringify({
      leadId: lead.id,
      currentColumnId: lead.stage,
      sourceCollection: lead.sourceCollection
    }));
  };

  const handleDragOver = (e) => {
    e.preventDefault(); // Necessary to allow dropping
  };

  const handleDrop = async (e, targetColumnId) => {
    e.preventDefault();
    try {
      const dataStr = e.dataTransfer.getData('application/json');
      if (!dataStr) return;
      
      const { leadId, currentColumnId, sourceCollection } = JSON.parse(dataStr);
      if (currentColumnId === targetColumnId) return;

      // Optimistic Snap
      setColumns(prev => {
        const sourceLeads = [...prev[currentColumnId].leads];
        const destLeads = [...prev[targetColumnId].leads];
        
        const leadIndex = sourceLeads.findIndex(l => l.id === leadId);
        if (leadIndex === -1) return prev;
        
        const [movedLead] = sourceLeads.splice(leadIndex, 1);
        movedLead.lastTouch = 'Just Now';
        movedLead.rawLastTouch = new Date();
        movedLead.stage = targetColumnId;
        
        if (movedLead.isUnscheduled && targetColumnId !== 'newLeads') {
          movedLead.isUnscheduled = false; 
        }
        destLeads.unshift(movedLead);

        return {
          ...prev,
          [currentColumnId]: { ...prev[currentColumnId], leads: sourceLeads },
          [targetColumnId]: { ...prev[targetColumnId], leads: destLeads }
        };
      });

      // Firebase Sync
      const firestore = db || getFirestore();
      const targetColl = sourceCollection || 'crm_leads';
      const leadRef = doc(firestore, 'clients', tenantId, targetColl, leadId);
      await updateDoc(leadRef, {
        stage: targetColumnId,
        status: targetColumnId,
        last_touch: serverTimestamp()
      });

    } catch (err) {
      console.error("Drop pipeline error:", err);
    }
  };

  // Keep legacy button move for accessibility
  const moveLead = async (e, leadId, currentColumnId, direction, sourceCollection) => {
    e.preventDefault(); 
    e.stopPropagation();

    const currentIdx = columnOrder.indexOf(currentColumnId);
    const nextIdx = currentIdx + direction;
    if (nextIdx < 0 || nextIdx >= columnOrder.length) return;

    const nextColumnId = columnOrder[nextIdx];

    setColumns(prev => {
      const sourceLeads = [...prev[currentColumnId].leads];
      const destLeads = [...prev[nextColumnId].leads];

      const leadIndex = sourceLeads.findIndex(l => l.id === leadId);
      if (leadIndex === -1) return prev;
      
      const [movedLead] = sourceLeads.splice(leadIndex, 1);
      movedLead.lastTouch = 'Just Now';
      movedLead.rawLastTouch = new Date();
      movedLead.stage = nextColumnId;

      if (movedLead.isUnscheduled && nextColumnId !== 'newLeads') {
        movedLead.isUnscheduled = false; 
      }
      destLeads.unshift(movedLead);

      return {
        ...prev,
        [currentColumnId]: { ...prev[currentColumnId], leads: sourceLeads },
        [nextColumnId]: { ...prev[nextColumnId], leads: destLeads }
      };
    });

    try {
      const firestore = db || getFirestore();
      const targetCollection = sourceCollection || 'crm_leads';
      const leadRef = doc(firestore, 'clients', tenantId, targetCollection, leadId);
      await updateDoc(leadRef, {
        stage: nextColumnId,
        status: nextColumnId,
        last_touch: serverTimestamp()
      });
    } catch (error) {
      console.error("Direct Firebase update failed on lead advance:", error);
    }
  };

  const archiveLead = async (e, leadId, currentColumnId, sourceCollection) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (window.confirm("Archive this deal? It will be permanently removed from the active pipeline.")) {
      setColumns(prev => {
        const sourceLeads = [...prev[currentColumnId].leads];
        const leadIndex = sourceLeads.findIndex(l => l.id === leadId);
        if (leadIndex === -1) return prev;
        sourceLeads.splice(leadIndex, 1);
        return {
          ...prev,
          [currentColumnId]: { ...prev[currentColumnId], leads: sourceLeads }
        };
      });

      try {
        const firestore = db || getFirestore();
        const targetCollection = sourceCollection || 'crm_leads';
        const leadRef = doc(firestore, 'clients', tenantId, targetCollection, leadId);
        await deleteDoc(leadRef);
      } catch (error) {
        console.error("Direct Firebase deletion failed:", error);
      }
    }
  };

  const cancelMeeting = async (leadId, sourceCollection) => {
    if (!window.confirm("Cancel this meeting? It will be removed from your schedule.")) return;
    try {
      const firestore = db || getFirestore();
      const targetCollection = sourceCollection || 'crm_leads';
      const leadRef = doc(firestore, 'clients', tenantId, targetCollection, leadId);

      await updateDoc(leadRef, {
        scheduledDate: null,
        scheduled_date: null,
        googleEventId: null,
        googleMeetLink: null,
        last_touch: serverTimestamp()
      });

      await fetchLivePipeline();
    } catch (error) {
      console.error("Failed to cancel meeting in Firebase:", error);
      alert("Failed to cancel meeting. Check console.");
    }
  };

  const handleOpenSchedule = () => {
    const offset = selectedDate.getTimezoneOffset();
    const localDate = new Date(selectedDate.getTime() - (offset * 60 * 1000));
    
    setScheduleForm({
      leadId: '',
      date: localDate.toISOString().split('T')[0],
      time: '12:00',
      assignedRep: currentUser?.email || '',
      clientEmail: ''
    });
    setShowScheduleModal(true);
  };

  const handleScheduleSubmit = async (e) => {
    e.preventDefault();
    setIsScheduling(true);
    try {
      const targetLead = allLeads.find(l => l.id === scheduleForm.leadId);
      if (!targetLead) throw new Error("Target lead not found.");

      const [year, month, day] = scheduleForm.date.split('-');
      const [hours, minutes] = scheduleForm.time.split(':');
      const combinedDate = new Date(year, month - 1, day, hours, minutes);

      const firestore = db || getFirestore();
      const targetCollection = targetLead.sourceCollection || 'crm_leads';
      const leadRef = doc(firestore, 'clients', tenantId, targetCollection, scheduleForm.leadId);

      await updateDoc(leadRef, {
        scheduledDate: combinedDate.toISOString(),
        assignedRep: scheduleForm.assignedRep || "",
        clientEmail: scheduleForm.clientEmail || "",
        isUnscheduled: false,
        stage: 'contacted',
        status: 'contacted',
        last_touch: serverTimestamp()
      });

      setShowScheduleModal(false);
      await fetchLivePipeline(); 
    } catch (error) {
      console.error("Failed to write schedule to Firebase:", error);
      alert("Failed to schedule event. Check console.");
    } finally {
      setIsScheduling(false);
    }
  };

  const daysInMonth = new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 0).getDate();
  const firstDayOfMonth = new Date(currentDate.getFullYear(), currentDate.getMonth(), 1).getDay();
  const monthNames = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
  
  const currentYear = new Date().getFullYear();
  const years = Array.from({ length: 10 }, (_, i) => currentYear - 2 + i);

  const generateCalendarDays = () => {
    const days = [];
    for (let i = 0; i < firstDayOfMonth; i++) days.push(null);
    for (let i = 1; i <= daysInMonth; i++) days.push(i);
    return days;
  };

  const getScheduledLeadsForDate = (dateObj) => {
    const targetTime = new Date(dateObj).setHours(0, 0, 0, 0);
    return allLeads.filter(lead => {
      if (lead.scheduledDateObj) {
        return new Date(lead.scheduledDateObj).setHours(0, 0, 0, 0) === targetTime;
      }
      return false;
    });
  };

  const scheduledForSelected = getScheduledLeadsForDate(selectedDate);
  
  const isViewingToday = 
    currentDate.getMonth() === new Date().getMonth() && 
    currentDate.getFullYear() === new Date().getFullYear() &&
    new Date(selectedDate).setHours(0, 0, 0, 0) === new Date().setHours(0, 0, 0, 0);

  const jumpToToday = () => {
    const today = new Date();
    setCurrentDate(today);
    setSelectedDate(today);
  };

  return (
    <div className="animate-in fade-in duration-500 relative flex flex-col h-[calc(100vh-120px)] min-h-0 font-sans">
      
      {/* HEADER SECTION */}
      <div className="flex justify-between items-end mb-4 border-b border-slate-300 dark:border-slate-700 pb-4 shrink-0">
        <div>
          <h1 className="text-2xl font-black text-slate-900 dark:text-white tracking-tight uppercase">
            {isOperatorView ? "My Sales Funnel" : "Global Deal Desk"}
          </h1>
          <p className="text-slate-500 dark:text-slate-400 text-sm mt-1 font-medium">
            {isOperatorView 
              ? "Advance leads to Deal Desk Review to submit provisioning payload." 
              : "Unified Operations: Monitor enterprise M&A logic and web traffic."}
          </p>
        </div>
        
        <div className="flex gap-8 items-end">
          <div className="hidden md:flex gap-6 border-r border-slate-300 dark:border-slate-700 pr-6">
            <div className="text-right">
              <p className="text-[9px] font-bold text-slate-400 uppercase tracking-widest mb-1">Active Pipeline</p>
              <p className="text-xl font-black text-slate-900 dark:text-white tracking-tight">
                {isLoading || tenantLoading ? '...' : `$${calculateTotalPipeline().toLocaleString()}`}
              </p>
            </div>
            <div className="text-right">
              <p className="text-[9px] font-bold text-slate-400 uppercase tracking-widest mb-1">Closed MRR</p>
              <p className="text-xl font-black text-emerald-600 dark:text-emerald-400 tracking-tight">
                {isLoading || tenantLoading ? '...' : `$${calculateClosedWon().toLocaleString()}`}
              </p>
            </div>
          </div>

          {isOperatorView ? (
            <div className="flex gap-2">
              <button 
                onClick={() => setShowLeadIntake(true)}
                className="bg-white dark:bg-slate-800 border border-slate-300 dark:border-slate-600 hover:border-slate-400 dark:hover:border-slate-500 text-slate-700 dark:text-slate-200 text-[10px] font-bold py-2.5 px-4 transition-colors uppercase tracking-widest rounded-sm shadow-sm"
              >
                + Quick Lead
              </button>
              <button 
                onClick={() => navigate('/operator/intake')}
                className="bg-emerald-600 hover:bg-emerald-500 text-white text-[10px] font-bold py-2.5 px-6 transition-colors uppercase tracking-widest rounded-sm shadow-sm flex items-center gap-2"
              >
                <i className="fa-solid fa-file-signature"></i> Submit Deal
              </button>
            </div>
          ) : (
            <div className="flex gap-2">
              <button 
                onClick={() => setShowLeadIntake(true)}
                className="bg-white dark:bg-slate-800 hover:bg-slate-50 dark:hover:bg-slate-700 text-slate-700 dark:text-slate-200 border border-slate-300 dark:border-slate-600 text-[10px] font-bold py-2.5 px-4 transition-colors uppercase tracking-widest rounded-sm shadow-sm flex items-center gap-2"
              >
                <i className="fa-solid fa-route text-amber-500"></i> Route Lead
              </button>
              <button 
                onClick={() => setShowGenesisIntake(true)}
                className="bg-slate-900 dark:bg-emerald-600 hover:bg-slate-800 dark:hover:bg-emerald-500 text-white text-[10px] font-bold py-2.5 px-6 transition-colors uppercase tracking-widest rounded-sm shadow-sm flex items-center gap-2"
              >
                <i className="fa-solid fa-bolt text-amber-400 dark:text-white"></i> Master Provisioning
              </button>
            </div>
          )}
        </div>
      </div>

      {/* EXECUTIVE HUD FILTERS */}
      <div className="flex items-center gap-4 mb-4 pb-4 shrink-0">
        <div className="flex items-center gap-2 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-sm px-3 py-1.5 shadow-sm">
          <i className="fa-solid fa-filter text-slate-400 text-[10px]"></i>
          <span className="text-[9px] font-black text-slate-500 uppercase tracking-widest">Deal Size:</span>
          <select 
            value={filterDealSize}
            onChange={(e) => setFilterDealSize(e.target.value)}
            className="bg-transparent text-[10px] font-black text-slate-900 dark:text-white outline-none uppercase tracking-widest cursor-pointer"
          >
            <option value="all">Global Pipeline</option>
            <option value="smb">SMB ( {"<"} $5k )</option>
            <option value="enterprise">Enterprise ( {">="} $5k )</option>
          </select>
        </div>

        {!isOperatorView && (
          <div className="flex items-center gap-2 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-sm px-3 py-1.5 shadow-sm">
            <i className="fa-solid fa-user-tie text-slate-400 text-[10px]"></i>
            <span className="text-[9px] font-black text-slate-500 uppercase tracking-widest">Assignee:</span>
            <select 
              value={filterRep}
              onChange={(e) => setFilterRep(e.target.value)}
              className="bg-transparent text-[10px] font-black text-slate-900 dark:text-white outline-none uppercase tracking-widest cursor-pointer max-w-[150px]"
            >
              <option value="all">All Operators</option>
              {uniqueReps.map(rep => <option key={rep} value={rep}>{rep}</option>)}
            </select>
          </div>
        )}
      </div>

      <div className="flex flex-1 gap-6 min-h-0">
        
        {/* KINETIC KANBAN BOARD */}
        <div className="flex gap-4 overflow-x-auto min-h-0 pb-4 flex-1 custom-scrollbar">
          {columnOrder.map((colId, index) => {
            const col = filteredColumns[colId];
            const displayLeads = col.leads;

            return (
              <div 
                key={col.id} 
                onDragOver={handleDragOver}
                onDrop={(e) => handleDrop(e, col.id)}
                className="bg-slate-50/50 dark:bg-slate-900/50 border border-slate-200 dark:border-slate-800 flex flex-col h-full rounded-sm shadow-sm min-h-0 min-w-[320px] w-[320px] shrink-0"
              >
                
                {/* Column Header */}
                <div className={`border-b ${col.borderClass} ${col.colorClass} px-4 py-3 flex justify-between items-center shrink-0`}>
                  <div className="flex items-center gap-2">
                    <h3 className={`text-[10px] font-black uppercase tracking-widest ${col.textClass}`}>{col.title}</h3>
                    <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded-sm bg-white/50 dark:bg-black/20 ${col.textClass}`}>
                      {displayLeads.length}
                    </span>
                  </div>
                  <span className={`text-xs font-black ${col.textClass}`}>
                    ${calculateColumnTotal(colId).toLocaleString()}
                  </span>
                </div>
                
                {/* Lead Cards Container */}
                <div className="p-3 overflow-y-auto space-y-3 flex-1 relative custom-scrollbar">
                  {(isLoading || tenantLoading) && (
                    <div className="absolute inset-0 bg-slate-50/50 dark:bg-slate-900/50 flex items-center justify-center z-10 backdrop-blur-[1px]">
                      <div className="w-5 h-5 border-2 border-emerald-500 border-t-transparent rounded-full animate-spin"></div>
                    </div>
                  )}

                  {!(isLoading || tenantLoading) && displayLeads.map(lead => {
                    
                    // Algorithmic Decay Calculation
                    const daysStagnant = Math.floor((new Date() - lead.rawLastTouch) / (1000 * 60 * 60 * 24));
                    let decayBorder = 'border-slate-200 dark:border-slate-700';
                    let decayTag = null;
                    
                    if (daysStagnant >= 7 && colId !== 'closedWon') {
                      decayBorder = 'border-rose-500 shadow-[0_0_8px_rgba(244,63,94,0.4)]';
                      decayTag = <span className="text-[8px] font-black bg-rose-50 text-rose-600 px-1.5 py-0.5 rounded-sm border border-rose-200 uppercase tracking-widest flex items-center gap-1"><i className="fa-solid fa-fire-flame-curved"></i> Stagnant {daysStagnant}d</span>;
                    } else if (daysStagnant >= 3 && colId !== 'closedWon') {
                      decayBorder = 'border-amber-400 shadow-[0_0_6px_rgba(251,191,36,0.3)]';
                    }

                    return (
                      <div 
                        key={lead.id} 
                        draggable
                        onDragStart={(e) => handleDragStart(e, lead)}
                        onClick={() => setActiveDrawerLead(lead)}
                        className={`bg-white dark:bg-slate-800 border ${decayBorder} p-4 rounded-sm hover:border-emerald-500 dark:hover:border-emerald-500 transition-all group relative flex flex-col cursor-pointer active:cursor-grabbing`}
                      >
                        <div className="flex justify-between items-start mb-2">
                          <h4 className="text-sm font-black text-slate-900 dark:text-white group-hover:text-emerald-700 dark:group-hover:text-emerald-400 transition-colors leading-tight pr-6 truncate pointer-events-none">
                            {lead.company}
                          </h4>
                          <button 
                            onClick={(e) => archiveLead(e, lead.id, col.id, lead.sourceCollection)}
                            className="text-slate-300 dark:text-slate-500 hover:text-red-500 dark:hover:text-red-400 transition-colors absolute top-4 right-4 opacity-0 group-hover:opacity-100 z-10"
                            title="Archive Deal"
                          >
                            <i className="fa-solid fa-xmark"></i>
                          </button>
                        </div>
                        
                        <div className="flex flex-col mb-4 gap-2 flex-1 pointer-events-none">
                          <p className="text-[11px] font-bold text-slate-500 dark:text-slate-400 truncate w-full">{lead.contact}</p>
                          
                          <div className="flex flex-wrap items-center gap-1.5 mt-1">
                            <span className={`text-[8px] font-bold px-1.5 py-0.5 uppercase tracking-widest rounded-sm flex items-center gap-1 ${
                              lead.sourceCollection === 'pipeline' 
                                ? 'bg-indigo-50 dark:bg-indigo-500/10 text-indigo-600 dark:text-indigo-400 border border-indigo-200 dark:border-indigo-500/20' 
                                : 'bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-300 border border-slate-200 dark:border-slate-600'
                            }`}>
                              <i className={`fa-solid ${lead.sourceCollection === 'pipeline' ? 'fa-globe' : 'fa-user-pen'} text-[8px]`}></i>
                              {lead.sourceType}
                            </span>

                            {decayTag}

                            {lead.isUnscheduled && !decayTag && (
                              <span className="text-[8px] font-bold bg-amber-50 dark:bg-amber-500/10 text-amber-600 dark:text-amber-400 px-1.5 py-0.5 uppercase tracking-widest rounded-sm border border-amber-200 dark:border-amber-500/20">
                                Unscheduled
                              </span>
                            )}
                            
                            {!isOperatorView && (
                              <span className="text-[8px] font-bold bg-slate-100 dark:bg-slate-700 text-slate-500 dark:text-slate-300 px-1.5 py-0.5 rounded-sm border border-slate-200 dark:border-slate-600 uppercase tracking-widest" title={`Rep: ${lead.rep}`}>
                                <i className="fa-solid fa-headset mr-1"></i>
                                {lead.rep.split(' ').map(n => n[0]).join('')}
                              </span>
                            )}
                          </div>
                        </div>
                        
                        <div className="flex justify-between items-end border-t border-slate-100 dark:border-slate-700 pt-3">
                          <div className="pointer-events-none">
                            <p className="text-sm font-black text-slate-900 dark:text-white tracking-tight">${lead.mrr.toLocaleString()} <span className="text-[9px] text-slate-400 font-bold uppercase tracking-widest ml-0.5">MRR</span></p>
                            <p className="text-[8px] font-bold text-slate-400 uppercase tracking-widest mt-1 flex items-center gap-1">
                              <i className="fa-regular fa-clock"></i> {lead.lastTouch}
                            </p>
                          </div>
                          
                          {/* Accessibility Click Arrows (Hidden on drag, optional use) */}
                          <div className="flex gap-1.5 z-10">
                            {index > 0 && (
                              <button 
                                onClick={(e) => moveLead(e, lead.id, col.id, -1, lead.sourceCollection)}
                                className="w-7 h-7 flex items-center justify-center rounded-sm border border-slate-200 dark:border-slate-600 bg-slate-50 dark:bg-slate-700 text-slate-400 dark:text-slate-300 hover:border-slate-400 dark:hover:border-slate-500 hover:text-slate-700 dark:hover:text-white transition-colors"
                                title="Move Back"
                              >
                                <i className="fa-solid fa-chevron-left text-[9px]"></i>
                              </button>
                            )}
                            {index < columnOrder.length - 1 && (
                              <button 
                                onClick={(e) => moveLead(e, lead.id, col.id, 1, lead.sourceCollection)}
                                className="w-7 h-7 flex items-center justify-center rounded-sm border border-slate-200 dark:border-emerald-500/30 bg-emerald-50 dark:bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 hover:bg-emerald-600 hover:text-white transition-colors"
                                title={col.id === 'negotiating' ? "Move to Deal Desk" : "Advance Deal"}
                              >
                                <i className="fa-solid fa-chevron-right text-[9px]"></i>
                              </button>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}

                  {!(isLoading || tenantLoading) && displayLeads.length === 0 && (
                    <div className="p-8 flex flex-col items-center justify-center text-center h-32 border border-dashed border-slate-300 dark:border-slate-700 rounded-sm bg-slate-50/50 dark:bg-slate-800/30">
                      <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">No active leads</p>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        {/* SCHEDULE HUB (Right Sidebar - Preserved exactly as requested) */}
        <div className="w-80 bg-white dark:bg-slate-800 border border-slate-300 dark:border-slate-700 rounded-sm shadow-sm flex flex-col min-h-0 shrink-0">
          <div className="p-5 border-b border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/80 flex flex-col justify-center items-center shrink-0">
            
            <div className="flex justify-between items-center w-full mb-6">
              <h3 className="text-[11px] font-black text-slate-900 dark:text-white uppercase tracking-widest flex items-center gap-2">
                <i className="fa-regular fa-calendar text-emerald-500"></i> Schedule Hub
              </h3>
              {!isViewingToday && (
                <button 
                  onClick={jumpToToday} 
                  className="text-[9px] font-bold text-slate-400 hover:text-emerald-600 dark:hover:text-emerald-400 uppercase tracking-widest transition-colors flex items-center gap-1"
                >
                  Jump to Today
                </button>
              )}
            </div>
            
            <div className="flex justify-between items-center mb-6 w-full px-1">
              <button 
                onClick={() => setCurrentDate(prev => new Date(prev.getFullYear(), prev.getMonth() - 1, 1))} 
                className="flex items-center justify-center w-6 h-6 rounded border border-slate-200 dark:border-slate-600 text-slate-600 dark:text-slate-300 hover:border-emerald-500 hover:text-emerald-600 transition-colors"
                title="Previous Month"
              >
                <i className="fa-solid fa-chevron-left text-[10px]"></i>
              </button>
              
              <div className="flex gap-2 items-center justify-center">
                <select 
                  value={currentDate.getMonth()}
                  onChange={(e) => setCurrentDate(new Date(currentDate.getFullYear(), parseInt(e.target.value), 1))}
                  className="appearance-none bg-transparent font-black text-slate-900 dark:text-white uppercase tracking-widest text-xs focus:outline-none cursor-pointer hover:text-emerald-600 dark:hover:text-emerald-400 transition-colors text-right"
                >
                  {monthNames.map((month, idx) => (
                    <option key={month} value={idx}>{month}</option>
                  ))}
                </select>
                <select 
                  value={currentDate.getFullYear()}
                  onChange={(e) => setCurrentDate(new Date(parseInt(e.target.value), currentDate.getMonth(), 1))}
                  className="appearance-none bg-transparent font-black text-slate-900 dark:text-white uppercase tracking-widest text-xs focus:outline-none cursor-pointer hover:text-emerald-600 dark:hover:text-emerald-400 transition-colors"
                >
                  {years.map(year => (
                    <option key={year} value={year}>{year}</option>
                  ))}
                </select>
              </div>

              <button 
                onClick={() => setCurrentDate(prev => new Date(prev.getFullYear(), prev.getMonth() + 1, 1))} 
                className="flex items-center justify-center w-6 h-6 rounded border border-slate-200 dark:border-slate-600 text-slate-600 dark:text-slate-300 hover:border-emerald-500 hover:text-emerald-600 transition-colors"
                title="Next Month"
              >
                <i className="fa-solid fa-chevron-right text-[10px]"></i>
              </button>
            </div>

            <div className="grid grid-cols-7 gap-1 text-center mb-2 w-full">
              {['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'].map(d => (
                <div key={d} className="text-[9px] font-bold text-slate-400 uppercase">{d}</div>
              ))}
            </div>
            
            <div className="grid grid-cols-7 gap-1 text-center w-full">
              {generateCalendarDays().map((day, idx) => {
                if (!day) return <div key={idx} className="p-1"></div>;
                
                const thisDate = new Date(currentDate.getFullYear(), currentDate.getMonth(), day);
                const isSelected = thisDate.setHours(0, 0, 0, 0) === new Date(selectedDate).setHours(0, 0, 0, 0);
                const isToday = thisDate.setHours(0, 0, 0, 0) === new Date().setHours(0, 0, 0, 0);
                const scheduledCalls = getScheduledLeadsForDate(thisDate);
                const callCount = scheduledCalls.length;

                return (
                  <button 
                    key={idx}
                    onClick={() => setSelectedDate(new Date(currentDate.getFullYear(), currentDate.getMonth(), day))}
                    className={`w-8 h-8 mx-auto flex items-center justify-center text-[11px] font-bold rounded-sm transition-all relative ${
                      isSelected ? 'bg-emerald-600 text-white shadow-sm' : 
                      isToday ? 'bg-slate-200 dark:bg-slate-700 text-slate-900 dark:text-white border border-slate-300 dark:border-slate-600' : 'text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700 border border-transparent'
                    }`}
                  >
                    {day}
                    {callCount > 0 && !isSelected && (
                      <span className="absolute -bottom-1 -right-1 w-3.5 h-3.5 bg-emerald-500 border border-white dark:border-slate-800 rounded-full text-[7px] text-white flex items-center justify-center font-black shadow-sm">
                        {callCount}
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
          </div>

          <div className="flex-1 overflow-y-auto bg-slate-50 dark:bg-slate-800/40 p-5 custom-scrollbar">
            <div className="flex justify-between items-center mb-4 border-b border-slate-200 dark:border-slate-700 pb-3">
              <h4 className="text-[10px] font-black text-slate-500 dark:text-slate-400 uppercase tracking-widest">
                Calls for {selectedDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
              </h4>
              <button 
                onClick={handleOpenSchedule}
                className="text-[9px] font-bold bg-emerald-50 dark:bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border border-emerald-200 dark:border-emerald-500/30 hover:bg-emerald-100 dark:hover:bg-emerald-500/20 px-2 py-1 rounded-sm uppercase tracking-widest transition-colors shadow-sm"
              >
                + Schedule
              </button>
            </div>
            
            {scheduledForSelected.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-32 text-slate-400 dark:text-slate-500">
                <i className="fa-regular fa-calendar-xmark text-2xl mb-3 opacity-50"></i>
                <p className="text-[10px] font-bold uppercase tracking-widest">No calls scheduled</p>
              </div>
            ) : (
              <div className="space-y-3">
                {scheduledForSelected.map(lead => (
                  <div key={lead.id} className="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 p-3 rounded-sm shadow-sm hover:border-emerald-500 transition-colors">
                    <p className="text-xs font-black text-slate-900 dark:text-white truncate">{lead.company}</p>
                    <p className="text-[10px] font-bold text-slate-500 dark:text-slate-400 truncate mb-3">{lead.contact}</p>
                    
                    <div className="flex justify-between items-center mb-3">
                      <span className="text-[9px] font-bold text-slate-600 dark:text-slate-300 bg-slate-100 dark:bg-slate-700 px-2 py-1 rounded-sm flex items-center gap-1.5 border border-slate-200 dark:border-slate-600">
                        <i className="fa-regular fa-clock text-[8px] text-emerald-600 dark:text-emerald-400"></i> 
                        {lead.scheduledDateObj ? lead.scheduledDateObj.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : 'Anytime'}
                      </span>
                    </div>

                    <div className="flex justify-between items-center border-t border-slate-100 dark:border-slate-700 pt-3">
                       <span className={`text-[8px] font-bold px-1.5 py-0.5 uppercase tracking-widest rounded-sm border ${lead.sourceCollection === 'pipeline' ? 'bg-indigo-50 dark:bg-indigo-500/10 text-indigo-600 dark:text-indigo-400 border-indigo-200 dark:border-indigo-500/20' : 'bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-300 border-slate-200 dark:border-slate-600'}`}>
                         {lead.sourceCollection === 'pipeline' ? 'Web' : 'Manual'}
                       </span>
                       <div className="flex items-center gap-3">
                         <button 
                           onClick={() => cancelMeeting(lead.id, lead.sourceCollection)}
                           className="text-[9px] font-bold text-slate-400 hover:text-red-500 dark:hover:text-red-400 transition-colors uppercase tracking-widest"
                           title="Cancel Meeting"
                         >
                           Cancel
                         </button>
                         <Link to={`/customer/${lead.id}`} className="text-[9px] font-bold text-emerald-600 dark:text-emerald-400 hover:text-emerald-700 dark:hover:text-emerald-300 uppercase tracking-widest">
                           Details <i className="fa-solid fa-arrow-right ml-1"></i>
                         </Link>
                       </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* LINEAR-STYLE SLIDE OUT DEAL DRAWER */}
      {activeDrawerLead && (
        <DealDrawer 
          lead={activeDrawerLead} 
          onClose={() => setActiveDrawerLead(null)} 
          onUpdate={fetchLivePipeline} 
        />
      )}

      {/* SCHEDULE MODAL */}
      {showScheduleModal && (
        <div className="fixed inset-0 bg-slate-900/40 dark:bg-slate-900/60 z-50 flex items-center justify-center p-4 backdrop-blur-sm animate-in fade-in">
          <div className="bg-white dark:bg-slate-800 border border-slate-300 dark:border-slate-600 shadow-xl rounded-sm w-full max-w-md flex flex-col overflow-hidden">
            <div className="p-5 border-b border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900 flex justify-between items-center">
              <h2 className="text-sm font-black text-slate-900 dark:text-white uppercase tracking-widest">Schedule Event</h2>
              <button onClick={() => setShowScheduleModal(false)} className="text-slate-400 hover:text-slate-700 dark:hover:text-white">
                <i className="fa-solid fa-xmark"></i>
              </button>
            </div>
            <form onSubmit={handleScheduleSubmit} className="p-6 space-y-5">
              <div>
                <label className="block text-[10px] font-bold text-slate-500 dark:text-slate-400 uppercase tracking-widest mb-1.5">Select Lead / Deal</label>
                <select 
                  required
                  value={scheduleForm.leadId}
                  onChange={(e) => {
                    const selectedId = e.target.value;
                    const targetLead = allLeads.find(l => l.id === selectedId);
                    setScheduleForm({
                      ...scheduleForm,
                      leadId: selectedId,
                      clientEmail: targetLead?.clientEmail || ''
                    });
                  }}
                  className="w-full border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-700 p-2.5 text-xs font-bold text-slate-800 dark:text-white focus:outline-none focus:border-emerald-500 rounded-sm shadow-inner cursor-pointer"
                >
                  <option value="">-- Choose a Lead --</option>
                  {allLeads.map(lead => (
                    <option key={lead.id} value={lead.id}>
                      {lead.company || lead.contact_name || lead.firstName} ({lead.sourceType || 'Lead'})
                    </option>
                  ))}
                </select>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-[10px] font-bold text-slate-500 dark:text-slate-400 uppercase tracking-widest mb-1.5">Date</label>
                  <input 
                    type="date" 
                    required
                    value={scheduleForm.date}
                    onChange={(e) => setScheduleForm({ ...scheduleForm, date: e.target.value })}
                    className="w-full border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-700 p-2.5 text-xs font-bold text-slate-800 dark:text-white focus:outline-none focus:border-emerald-500 rounded-sm shadow-inner cursor-pointer"
                  />
                </div>
                <div>
                  <label className="block text-[10px] font-bold text-slate-500 dark:text-slate-400 uppercase tracking-widest mb-1.5">Time</label>
                  <input 
                    type="time" 
                    required
                    value={scheduleForm.time}
                    onChange={(e) => setScheduleForm({ ...scheduleForm, time: e.target.value })}
                    className="w-full border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-700 p-2.5 text-xs font-bold text-slate-800 dark:text-white focus:outline-none focus:border-emerald-500 rounded-sm shadow-inner cursor-pointer"
                  />
                </div>
              </div>
              <div>
                <label className="block text-[10px] font-bold text-slate-500 dark:text-slate-400 uppercase tracking-widest mb-1.5">Assign To (Employee / Rep)</label>
                <input 
                  type="text" 
                  required
                  placeholder="e.g. Matt Reinhardt"
                  value={scheduleForm.assignedRep}
                  onChange={(e) => setScheduleForm({ ...scheduleForm, assignedRep: e.target.value })}
                  className="w-full border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-700 p-2.5 text-xs font-bold text-slate-800 dark:text-white focus:outline-none focus:border-emerald-500 rounded-sm shadow-inner"
                />
              </div>
              <div>
                <label className="block text-[10px] font-bold text-slate-500 dark:text-slate-400 uppercase tracking-widest mb-1.5">Client Email (For Google Meet Invite)</label>
                <input 
                  type="email" 
                  required
                  placeholder="client@example.com"
                  value={scheduleForm.clientEmail}
                  onChange={(e) => setScheduleForm({ ...scheduleForm, clientEmail: e.target.value })}
                  className="w-full border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-700 p-2.5 text-xs font-bold text-slate-800 dark:text-white focus:outline-none focus:border-emerald-500 rounded-sm shadow-inner"
                />
              </div>
              <div className="pt-5 mt-2 border-t border-slate-200 dark:border-slate-700 flex justify-end gap-3">
                <button 
                  type="button" 
                  onClick={() => setShowScheduleModal(false)}
                  className="px-5 py-2.5 text-[10px] font-bold text-slate-600 dark:text-slate-300 border border-slate-300 dark:border-slate-600 rounded-sm uppercase tracking-widest hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors shadow-sm"
                >
                  Cancel
                </button>
                <button 
                  type="submit" 
                  disabled={isScheduling}
                  className="px-6 py-2.5 bg-emerald-600 text-white rounded-sm text-[10px] font-bold uppercase tracking-widest hover:bg-emerald-500 transition-colors flex items-center gap-2 shadow-sm"
                >
                  {isScheduling ? <i className="fa-solid fa-spinner fa-spin"></i> : 'Save Event'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showLeadIntake && (
        <LeadIntakeForm 
          isOperatorView={isOperatorView} 
          onClose={() => { 
            setShowLeadIntake(false); 
            fetchLivePipeline(); 
          }} 
        />
      )}

      {showGenesisIntake && !isOperatorView && (
        <GenesisAccountCreator onClose={() => {
          setShowGenesisIntake(false);
          fetchLivePipeline();
        }} />
      )}
    </div>
  );
}